import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, MapPin, Phone, Star } from "lucide-react";
import { SITE } from "@/lib/site";
import { getCityPageWithService, RELATED_CITIES } from "@/lib/locations";
import useSEO from "@/hooks/useSEO";

export default function CityLanding({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const page = getCityPageWithService(slug);

  useSEO({
    title: page?.metaTitle || page?.title,
    description: page?.metaDescription,
    path: `/${slug}`,
    image: page?.service?.image,
    type: "website",
  });

  useEffect(() => {
    if (!page) return;
    const breadcrumb = document.createElement("script");
    breadcrumb.type = "application/ld+json";
    breadcrumb.id = "city-breadcrumb-jsonld";
    breadcrumb.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
        { "@type": "ListItem", position: 2, name: "Service Areas", item: `${SITE.url}/service-areas` },
        { "@type": "ListItem", position: 3, name: page.title, item: `${SITE.url}/${slug}` },
      ],
    });
    const service = document.createElement("script");
    service.type = "application/ld+json";
    service.id = "city-service-jsonld";
    service.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: page.title,
      description: page.metaDescription,
      image: page.service?.image,
      provider: {
        "@type": "LocalBusiness",
        name: SITE.name,
        telephone: SITE.phone,
        url: SITE.url,
        address: {
          "@type": "PostalAddress",
          addressLocality: SITE.address.locality,
          addressRegion: SITE.address.region,
          addressCountry: SITE.address.country,
        },
      },
      areaServed: { "@type": "City", name: page.city },
      url: `${SITE.url}/${slug}`,
    });
    document.head.appendChild(breadcrumb);
    document.head.appendChild(service);
    return () => { breadcrumb.remove(); service.remove(); };
  }, [page, slug]);

  if (!page) return <Navigate to="/services" replace />;
  const { service, city, intro, neighborhoods, whyHere, title } = page;
  const related = RELATED_CITIES(slug);

  return (
    <>
      <section className="relative pt-36 pb-24 overflow-hidden" data-testid={`city-${slug}-hero`}>
        <div className="absolute inset-0">
          <img src={service.image} alt={`${service.title} project in ${city} — Toronto Custom Signs & Wraps`} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/85 to-ink-950/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <nav className="text-[12px] uppercase tracking-[0.2em] text-white/55" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span className="mx-3 text-white/30">/</span>
            <Link to={`/services/${service.slug}`} className="hover:text-gold">{service.title}</Link>
            <span className="mx-3 text-white/30">/</span>
            <span className="text-gold">{city}</span>
          </nav>
          <div className="eyebrow gold-bar mt-6 inline-flex items-center gap-2"><MapPin size={12}/> Serving {city} & area</div>
          <h1 className="font-display text-5xl md:text-7xl mt-4 tracking-tight max-w-4xl">{title}</h1>
          <p className="text-white/70 mt-7 max-w-2xl text-lg leading-relaxed">{intro}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-gold" data-testid={`city-${slug}-quote`}>Get a {city} quote <ArrowUpRight size={14}/></Link>
            <a href={SITE.phoneHref} className="btn-ghost"><Phone size={14}/> {SITE.phone}</a>
          </div>
        </div>
      </section>

      {/* Why {city} */}
      <section className="py-20 bg-ink-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="eyebrow">Why {city} businesses choose us</div>
          <h2 className="font-display text-4xl md:text-5xl mt-3 tracking-tight">Built for {city}.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {whyHere.map((w, i) => (
              <div key={i} className="card-dark p-7" data-testid={`city-${slug}-why-${i}`}>
                <Star size={20} className="text-gold mb-5"/>
                <h3 className="font-display text-xl">{w.t}</h3>
                <p className="text-white/55 text-sm mt-3 leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's included (service capabilities) */}
      <section className="py-20 bg-ink-950">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">What's included</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 tracking-tight">{service.title} capabilities</h2>
            <p className="text-white/60 mt-5">Every project handled end-to-end — design, fabrication, and certified install.</p>
            <Link to={`/services/${service.slug}`} className="btn-ghost mt-8 inline-flex" data-testid={`city-${slug}-service-link`}>
              Full service details <ArrowUpRight size={14}/>
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {service.includes.map(item => (
              <div key={item} className="card-dark p-5 flex items-start gap-3">
                <Check size={16} className="text-gold mt-1"/>
                <span className="text-white/85">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-16 bg-ink-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="eyebrow">Neighborhoods we serve</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight">Across {city}.</h2>
          <div className="mt-8 flex flex-wrap gap-2">
            {neighborhoods.map(n => (
              <span key={n} className="px-4 py-2 text-[12px] uppercase tracking-[0.18em] text-white/70 border border-white/10 hover:border-gold hover:text-gold transition-colors">
                {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Related city pages — internal linking for SEO */}
      {related.length > 0 && (
        <section className="py-20 bg-ink-950">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="eyebrow">Also serving</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight">Nearby service areas</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {related.map(r => (
                <Link key={r.slug} to={`/${r.slug}`} className="card-dark p-5 flex items-center justify-between group" data-testid={`related-${r.slug}`}>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-gold">{r.city}</div>
                    <div className="text-white mt-1 font-medium">{r.title}</div>
                  </div>
                  <ArrowUpRight className="text-white/40 group-hover:text-gold transition-colors" size={18}/>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 bg-ink-900 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight">Ready to start your {city} project?</h2>
          <p className="text-white/60 max-w-xl mx-auto mt-5">Free quote and visual mockup. We respond within 24 hours.</p>
          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-gold">Request a quote <ArrowUpRight size={14}/></Link>
            <a href={SITE.phoneHref} className="btn-ghost">{SITE.phone}</a>
          </div>
        </div>
      </section>
    </>
  );
}
